import React, { useContext } from 'react'
import { sessionContext, useGetBusiness } from '@marketing-milk/frontend'
import { SurfacePalette } from '../../tokens'
import { useQueryClient } from 'react-query'
import { NarrativeBlock as NarrativeBlockData, Permissions } from '@marketing-milk/interfaces'
import { NarrativeEditor } from './NarrativeEditor'
import { SectionCard } from './SectionCard'
import { CreativeReviewHandoffCard } from './CreativeReviewHandoffCard'
import { useRefreshVideoAssets } from 'hooks/creatives/useRefreshVideoAssets'

interface Props {
  palette: SurfacePalette
  packetID: number
  sprintID?: number
  narrative: NarrativeBlockData[]
}

/**
 * Edit-mode Creative section: the strategy narrative plus the way through to the
 * review surface, which is where the sprint's creative is now both read and
 * written — the themes, which ads sit under them, and every verdict. The section
 * chrome keeps its packet-local `section` anchor (`<packetID>:creative`), where
 * existing section threads point.
 */
export const CreativeSection = ({ palette, packetID, sprintID, narrative }: Props) => {
  const business = useGetBusiness()
  const { user } = useContext(sessionContext)
  const queryClient = useQueryClient()

  // Internal-only recovery action: re-sync ad videos + creatives from Facebook to
  // repair missing video relations/URLs. Hidden from client/present view; server
  // still enforces business access on the sync endpoints regardless.
  const isStaff = !!user?.permissions.includes(Permissions.AccessAdminApp)
  const { refresh, isRefreshing } = useRefreshVideoAssets(business.id, {
    refetchCreatives: () =>
      queryClient.invalidateQueries(['sprints', 'sprint-creatives', sprintID, business.id]),
    showAuthorizerLink: isStaff,
  })

  return (
    <SectionCard
      palette={palette}
      sectionKey="creative"
      title="Creative"
      commentAnchor={{ type: 'section', id: `${packetID}:creative`, label: 'Creative' }}
    >
      {/* The overall strategy narrative — the section intro. It rides the same
        per-block narrative path as every other prose section; the server forces
        `locked` on it, so an AE edit is safe from the next regeneration without
        the editor needing to know locking exists. */}
      <div style={{ marginBottom: 24 }}>
        <NarrativeEditor
          palette={palette}
          sectionKey="creative-strategy"
          label="Creative strategy"
          packetID={packetID}
          narrative={narrative}
          placeholder="The thinking behind this sprint's creative…"
        />
      </div>
      <CreativeReviewHandoffCard palette={palette} businessID={business.id} packetID={packetID} />
      {isStaff && sprintID && (
        <button
          type="button"
          onClick={refresh}
          disabled={isRefreshing}
          style={{
            marginTop: 12,
            border: 'none',
            background: 'transparent',
            color: palette.textMuted,
            fontSize: 12,
            cursor: isRefreshing ? 'default' : 'pointer',
            opacity: isRefreshing ? 0.6 : 1,
            padding: 0,
          }}
        >
          <i className={`fa fa-${isRefreshing ? 'spinner fa-spin' : 'refresh'} mr-2`} />
          {isRefreshing ? 'Refreshing video assets…' : 'Refresh video assets'}
        </button>
      )}
    </SectionCard>
  )
}
